import { useRef, type KeyboardEvent, type ReactNode } from "react";
import { cx } from "../../lib/cx.js";

export interface TabItem<V extends string> {
  value: V;
  label: ReactNode;
  icon?: ReactNode;
  count?: number | null;
  disabled?: boolean;
}

interface TabsProps<V extends string> {
  items: TabItem<V>[];
  value: V;
  onChange: (v: V) => void;
  /** Prefijo para enlazar pestaña ↔ panel (aria-controls / aria-labelledby). */
  idPrefix: string;
  label: string;
  className?: string;
}

export const tabId = (prefix: string, v: string) => `${prefix}-tab-${v}`;
export const panelId = (prefix: string, v: string) => `${prefix}-panel-${v}`;

/**
 * Pestañas WAI-ARIA con foco itinerante: Tab entra a la pestaña activa, flechas /
 * Inicio / Fin se mueven entre ellas y las activan (activación automática).
 */
export function Tabs<V extends string>({ items, value, onChange, idPrefix, label, className }: TabsProps<V>) {
  const refs = useRef(new Map<string, HTMLButtonElement>());
  const enabled = items.filter((i) => !i.disabled);

  function onKeyDown(e: KeyboardEvent<HTMLButtonElement>) {
    const idx = enabled.findIndex((i) => i.value === value);
    let next: TabItem<V> | undefined;
    if (e.key === "ArrowRight") next = enabled[(idx + 1) % enabled.length];
    else if (e.key === "ArrowLeft") next = enabled[(idx - 1 + enabled.length) % enabled.length];
    else if (e.key === "Home") next = enabled[0];
    else if (e.key === "End") next = enabled[enabled.length - 1];
    if (!next) return;
    e.preventDefault();
    onChange(next.value);
    refs.current.get(next.value)?.focus();
  }

  return (
    <div
      role="tablist"
      aria-label={label}
      className={cx("relative flex gap-1 overflow-x-auto border-b border-border [scrollbar-width:thin]", className)}
    >
      {items.map((it) => {
        const active = it.value === value;
        return (
          <button
            key={it.value}
            ref={(el) => {
              if (el) refs.current.set(it.value, el);
              else refs.current.delete(it.value);
            }}
            type="button"
            role="tab"
            id={tabId(idPrefix, it.value)}
            aria-selected={active}
            aria-controls={panelId(idPrefix, it.value)}
            tabIndex={active ? 0 : -1}
            disabled={it.disabled}
            onClick={() => onChange(it.value)}
            onKeyDown={onKeyDown}
            className={cx(
              "-mb-px inline-flex shrink-0 items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium whitespace-nowrap transition-colors",
              "disabled:cursor-not-allowed disabled:opacity-45 [&>svg]:size-4",
              active ? "border-accent text-fg" : "border-transparent text-muted hover:border-border-strong hover:text-fg",
            )}
          >
            {it.icon}
            {it.label}
            {it.count !== undefined && it.count !== null && (
              <span className="rounded-full bg-surface-3 px-1.5 text-[11px] font-semibold text-muted tabular-nums">
                {it.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export function TabPanel({
  idPrefix, value, children, className,
}: { idPrefix: string; value: string; children: ReactNode; className?: string }) {
  return (
    <div
      role="tabpanel"
      id={panelId(idPrefix, value)}
      aria-labelledby={tabId(idPrefix, value)}
      tabIndex={0}
      className={cx("focus-visible:outline-offset-4", className)}
    >
      {children}
    </div>
  );
}
